/**
 * An editable table driven by a column spec, rendered with the legacy `.tbl` treatment.
 *
 * Most of the Configure screens are the same thing: a list of records with a few
 * inline-editable fields. One component means an edit behaves identically everywhere —
 * the same optimistic update, the same failure handling, the same read-only state for a
 * non-Admin.
 *
 * Column spec: { key, label, type?, options?, align?, render?, editable?, step?, width? }
 * `type` is 'text' | 'number' | 'select' | 'checkbox', or omit it for read-only. A `render`
 * function always wins, for derived or formatted values.
 */
import { useState } from 'react';
import { useModel } from '../state.jsx';

function Cell({ column, record, collection, onError }) {
  const { patch, isAdmin } = useModel();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const value = record[column.key];

  if (column.render) return column.render(record);

  if (!column.type || column.editable === false || !isAdmin) {
    return (
      <span className={column.type === 'number' ? 'num-mono' : undefined}>
        {value ?? '—'}{column.suffix ?? ''}
      </span>
    );
  }

  async function commit(next) {
    if (next === value) { setDraft(null); return; }
    setBusy(true);
    try {
      await patch(collection, record.id, { [column.key]: next });
      setDraft(null);
    } catch (err) {
      onError?.(err.message);
      setDraft(null);
    } finally {
      setBusy(false);
    }
  }

  if (column.type === 'checkbox') {
    return (
      <input type="checkbox" checked={!!value} disabled={busy}
        onChange={(e) => commit(e.target.checked)} />
    );
  }

  if (column.type === 'select') {
    return (
      <select className="ei" value={value ?? ''} disabled={busy}
        onChange={(e) => commit(e.target.value)}>
        {column.options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      className={'ei' + (column.type === 'number' ? ' num' : '')}
      type={column.type}
      step={column.step}
      value={draft ?? value ?? ''}
      disabled={busy}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(column.type === 'number' ? Number(e.target.value) : e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
      }}
    />
  );
}

export function DataTable({ collection, columns, records, empty = 'Nothing here yet.' }) {
  const [error, setError] = useState(null);

  if (!records.length) {
    return <div style={{ padding: 22, fontSize: 12, color: 'var(--ink3)' }}>{empty}</div>;
  }

  return (
    <>
      {error && (
        <div className="alert" style={{ background: 'var(--ltred)', color: 'var(--red)' }}>{error}</div>
      )}
      <div className="card">
        <div className="card-body flush">
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} style={{ width: c.width, textAlign: c.align }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id}>
                    {columns.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align }}>
                        <Cell column={c} record={record} collection={collection} onError={setError} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
